/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Input } from "@mui/material";
import { DateTimeEntry } from "./PeriodEntry";
import UriEntry, { DisplayFromFHIRUri } from "./UriEntry";
import CodeEntry from "./CodeEntry";
import StringEntry from "./StringEntry";
import PositiveIntEntry from "./PositiveIntEntry";
import DecimalEntry from "./DecimalEntry";
import Integer64Entry from "./Integer64Entry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import ViewPdfLauncher from "../components/ViewPdfLauncher";

const DisplayFromFHIRAttachment = ({ attachment }) => {
  if (attachment) {
    if (Array.isArray(attachment) || typeof attachment !== "object") {
      return <>[ERROR: Attachment data not interpretable.]</>;
    }
    let contentType = attachment.contentType || "";
    let language = attachment.language || "";
    let data = attachment.data || "";
    let url = attachment.url || "";
    let size = attachment.size || "";
    let hash = attachment.hash || "";
    let title = attachment.title || "";
    let creation = attachment.creation || "";
    let height = attachment.height || "";
    let width = attachment.width || "";
    let frames = attachment.frames || "";
    let duration = attachment.duration || "";
    let pages = attachment.pages || "";
    if (contentType === "application/pdf") {
      contentType = "PDF";
    }
    if (!title && contentType) {
      title = "Untitled " + contentType + " Attachment";
    }
    let divstyle = {};
    if (title) {
      divstyle = { "marginLeft": "24px" };
    }
    return <div>
      {title && <p><b>Title (of Attachment): </b> {title}</p>}
      <div style={divstyle}>
        {contentType && <p><b>Attachment Type: </b> {contentType}</p>}
        {language && <p><b>Language: </b> {language}</p>}
        {creation && <p><b>Date created: </b> {creation}</p>}
        {url && <p><b>URL: </b> <DisplayFromFHIRUri uri={url} /></p>}
        {(data && contentType === "PDF") &&
          <p><b>View PDF: </b> <ViewPdfLauncher label={title} pdfData={data} /></p>}
        {(hash || (data && contentType !== "PDF")) &&
          <p><b>Data available in base64Binary form.</b></p>}
        {size && <p><b>Attachement size: </b> {size} bytes</p>}
        {(height || width) && <p><b>Image size: </b> Height {height} pixels, Width {width} pixels</p>}
        {duration && <p><b>Duration: </b> {duration} seconds</p>}
        {frames && <p><b>Number of frames: </b> {frames}</p>}
        {pages && <p><b>Number of pages: </b> {pages}</p>}
      </div>
    </div>
  } else {
    return <></>
  }
}

const AttachmentEntry = ({ elementName, fieldLabel, startingValue, setResourceState, allowedContentTypes, allowedLanguages, dataEntryStyle, debug, startCollapsed }) => {

  const [attachmentState, setAttachmentState] = useState({});
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingAttachment = {
      "contentType": "", "language": "", "data": "", "url": "", "size": "", "hash": "",
      "title": "", "creation": "", "height": "", "width": "", "frames": "", "duration": "", "pages": ""
    };
    if (startingValue) {
      if (startingValue.extension) { startingAttachment.extension = startingValue.extension; }
      if (startingValue.contentType) { startingAttachment.contentType = startingValue.contentType; }
      if (startingValue.language) { startingAttachment.language = startingValue.language; }
      if (startingValue.data) { startingAttachment.data = startingValue.data; }
      if (startingValue.url) { startingAttachment.url = startingValue.url; }
      if (startingValue.size) { startingAttachment.size = startingValue.size; }
      if (startingValue.hash) { startingAttachment.hash = startingValue.hash; }
      if (startingValue.title) { startingAttachment.title = startingValue.title; }
      if (startingValue.creation) { startingAttachment.creation = startingValue.creation; }
      if (startingValue.height) { startingAttachment.height = startingValue.height; }
      if (startingValue.width) { startingAttachment.width = startingValue.width; }
      if (startingValue.frames) { startingAttachment.frames = startingValue.frames; }
      if (startingValue.duration) { startingAttachment.duration = startingValue.duration; }
      if (startingValue.pages) { startingAttachment.pages = startingValue.pages; }
    }
    setAttachmentState(JSON.parse(JSON.stringify(startingAttachment)));
  }, []);

  useEffect((() => {
    if (Object.keys(attachmentState).length) {
      let newAttachment = {};
      if (attachmentState.extension) { newAttachment.extension = attachmentState.extension; }
      if (attachmentState.contentType) { newAttachment.contentType = attachmentState.contentType; }
      if (attachmentState.language) { newAttachment.language = attachmentState.language; }
      if (attachmentState.data) { newAttachment.data = attachmentState.data; }
      if (attachmentState.url) { newAttachment.url = attachmentState.url; }
      if (attachmentState.size) { newAttachment.size = attachmentState.size; }
      if (attachmentState.hash) { newAttachment.hash = attachmentState.hash; }
      if (attachmentState.title) { newAttachment.title = attachmentState.title; }
      if (attachmentState.creation) { newAttachment.creation = attachmentState.creation; }
      if (attachmentState.height) { newAttachment.height = attachmentState.height; }
      if (attachmentState.width) { newAttachment.width = attachmentState.width; }
      if (attachmentState.frames) { newAttachment.frames = attachmentState.frames; }
      if (attachmentState.duration) { newAttachment.duration = attachmentState.duration; }
      if (attachmentState.pages) { newAttachment.pages = attachmentState.pages; }
      if (Object.keys(newAttachment).length === 0) {
        newAttachment = null;
      }
      setResourceState(prevState => { return { ...prevState, [elementName]: newAttachment } });
    }
  }), [attachmentState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRAttachment attachment={startingValue} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else if (Object.keys(attachmentState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <Input id="fileAttachment" type="file"
          onChange={(e, data) => {
            if (e.target.files.length > 0) {
              let file = e.target.files[0];
              if (file.name.length >= 5 && file.name.slice(-4).toLowerCase() === ".pdf") {
                let reader = new FileReader();
                reader.readAsDataURL(e.target.files[0]);
                reader.onload = function () {
                  if (reader.result.length && reader.result.length < 70000000) {
                    setAttachmentState(prevState => {
                      return {
                        ...prevState,
                        contentType: 'application/pdf',
                        data: reader.result.replace("data:application/pdf;base64,", ""),
                        size: reader.result.length.toString(),
                        title: file.name.slice(0, -4)
                      };
                    });
                  } else {
                    alert("We only support attaching files that are 50 MB or less. If your file is, then please email support@computablepublishing.com");
                  }
                };
                reader.onerror = function (error) {
                  console.log('Error, ', error);
                };
              } else if (dataEntryStyle === 'pdfonly') {
                alert("We currently only support .pdf files. Please choose another file.");
                //TODO: selected file name still shows in the data entry space, should be cleared.
              } else {
                let reader = new FileReader();
                reader.readAsDataURL(e.target.files[0]);
                reader.onload = function () {
                  if (reader.result.length && reader.result.length < 70000000) {
                    setAttachmentState(prevState => {
                      let resultTwoParts = reader.result.split(',', 2);
                      let resultHeaderTwoParts = resultTwoParts[0].split(':', 2);
                      return {
                        ...prevState,
                        contentType: resultHeaderTwoParts[1],
                        data: resultTwoParts[1],
                        size: reader.result.length.toString(),
                        title: file.name
                      };
                    });
                  } else {
                    alert("We only support attaching files that are 50 MB or less. If your file is, then please email support@computablepublishing.com");
                  }
                };
                reader.onerror = function (error) {
                  console.log('Error, ', error);
                };
              }
            }
          }}
        />
        {allowedContentTypes ?
          <CodeEntry elementName='contentType'
            allowedValues={allowedContentTypes} dataEntryStyle='dropdown'
            fieldLabel='Content Type (Mime type)' startingValue={attachmentState.contentType} setResourceState={setAttachmentState} />
          :
          <StringEntry elementName='contentType'
            fieldLabel='Content Type (Mime type)' startingValue={attachmentState.contentType} setResourceState={setAttachmentState} />
        }
        {allowedLanguages ?
          <CodeEntry elementName='language'
            allowedValues={allowedLanguages} dataEntryStyle='dropdown'
            fieldLabel='Language (Human language)' startingValue={attachmentState.language} setResourceState={setAttachmentState} />
          :
          <StringEntry elementName='language'
            fieldLabel='Language (Human language)' startingValue={attachmentState.language} setResourceState={setAttachmentState} />
        }
        <StringEntry elementName='data'
          fieldLabel='Data (base64Binary)' startingValue={attachmentState.data} setResourceState={setAttachmentState} />
        <UriEntry elementName='url'
          fieldLabel='URL' startingValue={attachmentState.url} setResourceState={setAttachmentState} />
        <Integer64Entry elementName='size'
          fieldLabel='Size (# of bytes)' startingValue={attachmentState.size} setResourceState={setAttachmentState} />
        <StringEntry elementName='hash'
          fieldLabel='Hash (sha-1, base64Binary)' startingValue={attachmentState.hash} setResourceState={setAttachmentState} />
        <StringEntry elementName='title'
          fieldLabel='Title' startingValue={attachmentState.title} setResourceState={setAttachmentState} />
        <DateTimeEntry elementName='creation'
          fieldLabel='Creation date' startingValue={attachmentState.creation} setResourceState={setAttachmentState} />
        <PositiveIntEntry elementName='height'
          fieldLabel='Height (in pixels)' startingValue={attachmentState.height} setResourceState={setAttachmentState} />
        <PositiveIntEntry elementName='width'
          fieldLabel='Width (in pixels)' startingValue={attachmentState.width} setResourceState={setAttachmentState} />
        <PositiveIntEntry elementName='frames'
          fieldLabel='Number of frames' startingValue={attachmentState.frames} setResourceState={setAttachmentState} />
        <DecimalEntry elementName='duration'
          fieldLabel='Duration (in seconds)' startingValue={attachmentState.duration} setResourceState={setAttachmentState} />
        <PositiveIntEntry elementName='pages'
          fieldLabel='Number of pages' startingValue={attachmentState.pages} setResourceState={setAttachmentState} />
      </div>
    </>
  }
}

export default AttachmentEntry;
export { DisplayFromFHIRAttachment };